import { Button, ButtonGroup, Heading, IconButton, Input, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type Project } from "@overmap-ai/common"
import { selectAccessibleProjects, selectCurrentUser } from "@overmap-ai/core"
import { useTextFilter } from "@overmap-ai/hooks"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { memo, useCallback, useMemo } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { ProjectFilterMenu } from "../../components/FilterMenus/ProjectFilterMenu"
import { useProjectTable, useProjectTableColumns } from "../../components/Tables"
import { ColumnVisibilityDropdown } from "../../components/Tables/components"
import { tableIconMapping } from "../../components/Tables/mappings"
import { Table } from "../../components/Tables/Table"
import { FilterRenderer } from "../../filtering/components"
import type { AnyFilter } from "../../filtering/filter"
import { useFilterManager, useFilters } from "../../filtering/hooks"
import { projectFilterSchema } from "../../filtering/schemas"
import { projectFilterCallback } from "../../filtering/utils"
import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { setProjectFilters, useAppDispatch, useAppSelector, useShallowEqaulSelector } from "../../store"
import { selectProjectFilters } from "../../store/selectors"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/projects/")

export const ProjectsView = memo(() => {
	const { organizationId: _organizationId } = routeApi.useParams()

	const currentUser = useAppSelector(selectCurrentUser)!
	const projects = useShallowEqaulSelector(selectAccessibleProjects(currentUser.id))
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const serializedFilters = useAppSelector(selectProjectFilters)

	const filters = useMemo(() => {
		return projectFilterSchema.deserializeFilters(serializedFilters)
	}, [serializedFilters])

	const handleFiltersChange = useCallback(
		(filters: AnyFilter[]) => {
			dispatch(setProjectFilters(projectFilterSchema.serializeFilters(filters)))
		},
		[dispatch],
	)

	const filterManager = useFilterManager({
		filterSchema: projectFilterSchema,
		filters,
		onFilterChange: handleFiltersChange,
	})

	const filteredProjects = useFilters({
		filterManager,
		data: projects,
		filters,
	})

	const [searchedProjects, filterValue, setFilterValue] = useTextFilter(filteredProjects, projectFilterCallback)

	const handleProjectClick = useCallback(
		(project: Project) => {
			void navigate({
				from: "/$organizationId/projects/",
				to: "/$organizationId/$projectId",
				params: { projectId: project.uuid },
			})
		},
		[navigate],
	)

	const columns = useProjectTableColumns()

	const table = useProjectTable({
		data: searchedProjects,
		columns,
		enableHiding: true,
	})

	return (
		<ViewContainer maxWidth="6xl">
			<div className="flex size-full flex-col gap-4">
				<div className="flex h-max w-full flex-col gap-1">
					<div className="flex h-max w-full items-end justify-between gap-2">
						<Heading size="xl">Projects</Heading>
					</div>

					<Text accentColor="base" size="sm">
						Click on a project to switch to it.
					</Text>
				</div>

				<Separator size="full" />

				<div className="flex min-h-0 w-full grow flex-col gap-2">
					<ButtonGroup className="flex justify-between gap-2" variant="soft" accentColor="base" size="sm">
						<div className="flex grow items-center gap-2">
							<Input.Root variant="outline">
								<Input.Slot>
									<LuIcon icon={modelIconMapping.search} />
								</Input.Slot>
								<Input.Field
									placeholder="Search"
									value={filterValue}
									onChange={useChangeEventHandler(setFilterValue)}
								/>
							</Input.Root>
							<ProjectFilterMenu filterManager={filterManager} align="start" size="sm">
								<Button>
									<LuIcon icon={modelIconMapping.filter} />
									Filter
								</Button>
							</ProjectFilterMenu>

							{filterManager.getFilters().length > 0 && (
								<IconButton onClick={filterManager.removeAll}>
									<LuIcon icon={modelIconMapping.close} />
								</IconButton>
							)}
						</div>

						<ColumnVisibilityDropdown table={{ ...table }} align="end" size="sm">
							<IconButton>
								<LuIcon icon={tableIconMapping.columns} />
							</IconButton>
						</ColumnVisibilityDropdown>
					</ButtonGroup>

					<FilterRenderer filterManager={filterManager} size="sm" />

					<div className="min-h-0 w-full grow overflow-auto">
						<Table
							className="w-full"
							table={{ ...table }}
							onRowClick={handleProjectClick}
							variant="surface"
							size="sm"
							interactive
							emptyText="No projects."
						/>
					</div>
				</div>
			</div>
		</ViewContainer>
	)
})

ProjectsView.displayName = "ProjectsView"
